/**
 * Motion Design Tokens
 * Animation durations, easing functions, and animation presets
 * Based on DESIGN_TOKENS.md specification
 */

export const motion = {
  // Animation durations (in milliseconds)
  duration: {
    fast: 150,        // Quick feedback
    normal: 300,      // Standard transitions
    slow: 500,        // Emphasized animations
    slower: 700,      // Entrance effects
  },

  // Easing functions (cubic-bezier curves)
  easing: {
    linear: 'linear',
    ease: 'ease',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },

  // Animation delays (in milliseconds)
  delay: {
    0: '0ms',
    50: '50ms',
    100: '100ms',
    150: '150ms',
    200: '200ms',
    300: '300ms',
    stagger: '50ms',  // Per child element
  },

  // Semantic animation presets
  animations: {
    // Entrance animations
    fadeIn: {
      keyframes: 'fadeIn',
      duration: 300,
      easing: 'easeOut',
    },
    fadeInScale: {
      keyframes: 'fadeInScale',
      duration: 300,
      easing: 'easeOut',
    },
    slideInUp: {
      keyframes: 'slideInUp',
      duration: 400,
      easing: 'easeOut',
    },
    slideInDown: {
      keyframes: 'slideInDown',
      duration: 400,
      easing: 'easeOut',
    },
    slideInLeft: {
      keyframes: 'slideInLeft',
      duration: 400,
      easing: 'easeOut',
    },
    slideInRight: {
      keyframes: 'slideInRight',
      duration: 400,
      easing: 'easeOut',
    },
    scaleIn: {
      keyframes: 'scaleIn',
      duration: 300,
      easing: 'easeOut',
    },
    scaleInRotate: {
      keyframes: 'scaleInRotate',
      duration: 400,
      easing: 'easeOut',
    },
    rotateIn: {
      keyframes: 'rotateIn',
      duration: 600,
      easing: 'easeOut',
    },
    rotateInScale: {
      keyframes: 'rotateInScale',
      duration: 500,
      easing: 'easeOut',
    },
    flipInX: {
      keyframes: 'flipInX',
      duration: 600,
      easing: 'easeOut',
    },
    zoomIn: {
      keyframes: 'zoomIn',
      duration: 300,
      easing: 'easeOut',
    },
    zoomInScale: {
      keyframes: 'zoomInScale',
      duration: 400,
      easing: 'easeOut',
    },
    bounceIn: {
      keyframes: 'bounceIn',
      duration: 750,
      easing: 'easeOut',
    },

    // Exit animations
    fadeOut: {
      keyframes: 'fadeOut',
      duration: 250,
      easing: 'easeIn',
    },
    fadeOutScale: {
      keyframes: 'fadeOutScale',
      duration: 250,
      easing: 'easeIn',
    },
    slideOutUp: {
      keyframes: 'slideOutUp',
      duration: 300,
      easing: 'easeIn',
    },
    slideOutDown: {
      keyframes: 'slideOutDown',
      duration: 300,
      easing: 'easeIn',
    },
    slideOutLeft: {
      keyframes: 'slideOutLeft',
      duration: 300,
      easing: 'easeIn',
    },
    slideOutRight: {
      keyframes: 'slideOutRight',
      duration: 300,
      easing: 'easeIn',
    },
    scaleOut: {
      keyframes: 'scaleOut',
      duration: 250,
      easing: 'easeIn',
    },
    rotateOut: {
      keyframes: 'rotateOut',
      duration: 400,
      easing: 'easeIn',
    },
    flipOutX: {
      keyframes: 'flipOutX',
      duration: 500,
      easing: 'easeIn',
    },
    zoomOut: {
      keyframes: 'zoomOut',
      duration: 300,
      easing: 'easeIn',
    },
    bounceOut: {
      keyframes: 'bounceOut',
      duration: 750,
      easing: 'easeOut',
    },

    // Status & feedback animations
    pulse: {
      keyframes: 'pulse',
      duration: 2000,
      easing: 'linear',
      iterationCount: 'infinite',
    },
    pulse2: {
      keyframes: 'pulse',
      duration: 3000,
      easing: 'ease-in-out',
      iterationCount: 'infinite',
    },
    heartbeat: {
      keyframes: 'heartbeat',
      duration: 1300,
      easing: 'ease-in-out',
      iterationCount: 'infinite',
    },
    ping: {
      keyframes: 'ping',
      duration: 1000,
      easing: 'cubic-bezier(0, 0, 0.2, 1)',
      iterationCount: 'infinite',
    },

    // Loading animations
    shimmer: {
      keyframes: 'shimmer',
      duration: 2000,
      easing: 'linear',
      iterationCount: 'infinite',
    },
    spin: {
      keyframes: 'spin',
      duration: 1000,
      easing: 'linear',
      iterationCount: 'infinite',
    },

    // Attention animations
    bounce: {
      keyframes: 'bounce',
      duration: 500,
      easing: 'easeInOut',
    },
    jelly: {
      keyframes: 'jelly',
      duration: 900,
      easing: 'ease-in-out',
    },
    swing: {
      keyframes: 'swing',
      duration: 1000,
      easing: 'ease-in-out',
    },
    wobble: {
      keyframes: 'wobble',
      duration: 800,
      easing: 'ease-in-out',
    },
    wiggle: {
      keyframes: 'wiggle',
      duration: 600,
      easing: 'ease-in-out',
    },
    flip: {
      keyframes: 'flip',
      duration: 1000,
      easing: 'ease-in-out',
    },

    // Smooth transitions
    smoothTransition: {
      duration: 300,
      easing: 'easeInOut',
    },
  },
};

// CSS Keyframes definitions (to be injected into global styles)
export const keyframes = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }

  @keyframes fadeInScale {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }

  @keyframes fadeOutScale {
    from { opacity: 1; transform: scale(1); }
    to { opacity: 0; transform: scale(0.95); }
  }

  @keyframes slideInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes slideOutDown {
    from { opacity: 1; transform: translateY(0); }
    to { opacity: 0; transform: translateY(20px); }
  }

  @keyframes slideInDown {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes slideOutUp {
    from { opacity: 1; transform: translateY(0); }
    to { opacity: 0; transform: translateY(-20px); }
  }

  @keyframes slideInLeft {
    from { opacity: 0; transform: translateX(-20px); }
    to { opacity: 1; transform: translateX(0); }
  }

  @keyframes slideOutRight {
    from { opacity: 1; transform: translateX(0); }
    to { opacity: 0; transform: translateX(20px); }
  }

  @keyframes slideInRight {
    from { opacity: 0; transform: translateX(20px); }
    to { opacity: 1; transform: translateX(0); }
  }

  @keyframes slideOutLeft {
    from { opacity: 1; transform: translateX(0); }
    to { opacity: 0; transform: translateX(-20px); }
  }

  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }

  @keyframes scaleOut {
    from { opacity: 1; transform: scale(1); }
    to { opacity: 0; transform: scale(0.95); }
  }

  @keyframes scaleInRotate {
    from { opacity: 0; transform: scale(0.95) rotate(-5deg); }
    to { opacity: 1; transform: scale(1) rotate(0); }
  }

  @keyframes rotateIn {
    from { opacity: 0; transform: rotate(-5deg); }
    to { opacity: 1; transform: rotate(0); }
  }

  @keyframes rotateOut {
    from { opacity: 1; transform: rotate(0); }
    to { opacity: 0; transform: rotate(5deg); }
  }

  @keyframes rotateInScale {
    from { opacity: 0; transform: scale(0.8) rotate(-12deg); }
    to { opacity: 1; transform: scale(1) rotate(0); }
  }

  @keyframes rotateOutScale {
    from { opacity: 1; transform: scale(1) rotate(0); }
    to { opacity: 0; transform: scale(0.8) rotate(12deg); }
  }

  @keyframes flip {
    0% { transform: perspective(400px) rotateY(0); }
    100% { transform: perspective(400px) rotateY(360deg); }
  }

  @keyframes flipInX {
    from { opacity: 0; transform: perspective(400px) rotateY(90deg); }
    to { opacity: 1; transform: perspective(400px) rotateY(0); }
  }

  @keyframes flipOutX {
    from { opacity: 1; transform: perspective(400px) rotateY(0); }
    to { opacity: 0; transform: perspective(400px) rotateY(90deg); }
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  @keyframes heartbeat {
    0% { transform: scale(1); }
    14% { transform: scale(1.3); }
    28% { transform: scale(1); }
    42% { transform: scale(1.3); }
    70% { transform: scale(1); }
  }

  @keyframes ping {
    75%, 100% { transform: scale(2); opacity: 0; }
  }

  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }

  @keyframes bounceIn {
    0% { opacity: 0; transform: scale(0.3); }
    50% { opacity: 1; transform: scale(1.05); }
    70% { transform: scale(0.9); }
    100% { transform: scale(1); }
  }

  @keyframes bounceOut {
    0% { transform: scale(1); }
    25% { transform: scale(0.95); }
    50% { opacity: 1; transform: scale(1.1); }
    100% { opacity: 0; transform: scale(0.3); }
  }

  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes swing {
    20% { transform: rotate(15deg); }
    40% { transform: rotate(-10deg); }
    60% { transform: rotate(5deg); }
    80% { transform: rotate(-5deg); }
    100% { transform: rotate(0deg); }
  }

  @keyframes wobble {
    0% { transform: translateX(0%); }
    15% { transform: translateX(-5px) rotate(-5deg); }
    30% { transform: translateX(3px) rotate(3deg); }
    45% { transform: translateX(-3px) rotate(-3deg); }
    60% { transform: translateX(2px) rotate(2deg); }
    75% { transform: translateX(-1px) rotate(-1deg); }
    100% { transform: translateX(0%); }
  }

  @keyframes jelly {
    0%, 11.1%, 100% { transform: none; }
    22.2% { transform: skewX(-12.5deg) skewY(-12.5deg); }
    33.3% { transform: skewX(6.25deg) skewY(6.25deg); }
    44.4% { transform: skewX(-3.125deg) skewY(-3.125deg); }
    55.5% { transform: skewX(1.5625deg) skewY(1.5625deg); }
    66.6% { transform: skewX(-0.78125deg) skewY(-0.78125deg); }
    77.7% { transform: skewX(0.390625deg) skewY(0.390625deg); }
    88.8% { transform: skewX(-0.1953125deg) skewY(-0.1953125deg); }
  }

  @keyframes zoomIn {
    from { opacity: 0; transform: scale3d(0.3, 0.3, 0.3); }
    50% { opacity: 1; }
    to { transform: scale3d(1, 1, 1); }
  }

  @keyframes zoomOut {
    from { transform: scale3d(1, 1, 1); }
    50% { opacity: 1; }
    to { opacity: 0; transform: scale3d(0.3, 0.3, 0.3); }
  }

  @keyframes zoomInScale {
    from { opacity: 0; transform: scale(0.3) rotate(-180deg); }
    to { opacity: 1; transform: scale(1) rotate(0); }
  }

  @keyframes wiggle {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
  }
`;

/**
 * Animation Utility Functions
 * Helper functions for creating consistent animation styles in MUI sx props
 */

/** Create animation string for sx prop */
export const createAnimation = (
  name: keyof typeof motion.animations,
  delayMs = 0,
  fillMode: 'forwards' | 'backwards' | 'both' | 'none' = 'forwards'
): string => {
  const anim = motion.animations[name];
  if (!anim || !('keyframes' in anim)) return 'none';
  
  const duration = anim.duration || 300;
  const easing = motion.easing[anim.easing as keyof typeof motion.easing] || motion.easing.easeOut;
  const iterations = (anim as { iterationCount?: string }).iterationCount || '1';
  
  return `${anim.keyframes} ${duration}ms ${easing} ${delayMs}ms ${iterations} ${fillMode}`;
};

/** Create staggered animation delay based on index */
export const staggerDelay = (index: number, baseDelay = 0, staggerMs = 50): number => {
  return baseDelay + (index * staggerMs);
};

/** Animation sx props for common entrance animations */
export const entranceAnimation = {
  fadeIn: (delayMs = 0) => ({
    animation: createAnimation('fadeIn', delayMs),
  }),
  
  fadeInScale: (delayMs = 0) => ({
    animation: createAnimation('fadeInScale', delayMs),
  }),
  
  slideInUp: (delayMs = 0) => ({
    animation: createAnimation('slideInUp', delayMs),
  }),
  
  scaleIn: (delayMs = 0) => ({
    animation: createAnimation('scaleIn', delayMs),
  }),
};

/** Create staggered animation styles for list items */
export const staggeredEntrance = (
  index: number,
  animationType: 'fadeIn' | 'fadeInScale' | 'slideInUp' | 'scaleIn' = 'slideInUp',
  baseDelay = 0,
  staggerMs = 50
) => {
  const delay = staggerDelay(index, baseDelay, staggerMs);
  return entranceAnimation[animationType](delay);
};

/** Hover transition presets for interactive elements */
export const hoverTransition = {
  lift: {
    transition: `all ${motion.duration.fast}ms ${motion.easing.easeOut}`,
    '&:hover': {
      transform: 'translateY(-4px)',
    },
  },
  
  scale: {
    transition: `all ${motion.duration.fast}ms ${motion.easing.easeOut}`,
    '&:hover': {
      transform: 'scale(1.02)',
    },
  },
  
  liftScale: {
    transition: `all ${motion.duration.fast}ms ${motion.easing.easeOut}`,
    '&:hover': {
      transform: 'translateY(-4px) scale(1.01)',
    },
  },
  
  glow: (glowColor: string) => ({
    transition: `all ${motion.duration.fast}ms ${motion.easing.easeOut}`,
    '&:hover': {
      boxShadow: glowColor,
    },
  }),
  
  liftGlow: (glowColor: string) => ({
    transition: `all ${motion.duration.fast}ms ${motion.easing.easeOut}`,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: glowColor,
    },
  }),
};

/** Smooth transition preset for general use */
export const smoothTransition = (
  properties = 'all',
  duration = motion.duration.normal,
  easing = motion.easing.easeInOut
) => ({
  transition: `${properties} ${duration}ms ${easing}`,
});

export type Motion = typeof motion;
